import { type Currency, formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { CurrencyIcon } from "@loot-os/ui/components/currency-icon";
import { formatNumber } from "@loot-os/ui/lib/format";
import { cn } from "@loot-os/ui/lib/utils";

/**
 * A box as it appears in the storefront grid.
 *
 * Structurally typed rather than taking a `BoxSummary`, so the dashboard can
 * preview a draft that has no id or slug yet.
 *
 * Renders no link. The storefront and the dashboard use different routers, so
 * the caller wraps this in whichever `<Link>` it has — `group` lives on the
 * card's own root, which means the hover affordance also works unwrapped, in
 * the dashboard preview.
 *
 * The price is a button in the design but a `<div>` here, styled to match: the
 * caller wraps the whole card in a link, and an interactive element nested
 * inside an anchor is invalid HTML and unreachable by keyboard.
 */
export function BoxCard({
  box,
  currency,
  className,
}: {
  box: {
    name: string;
    imageUrl: string;
    /** Integer US cents. */
    priceCents: number;
    openedCount?: number;
    isCatalog?: boolean;
  };
  /** The environment's display currency; the price and its icon come from it. */
  currency: Currency;
  className?: string;
}) {
  return (
    <Card
      className={cn(
        "group h-full gap-3 overflow-hidden py-0 transition-colors hover:border-primary/50",
        className,
      )}
    >
      <div className="relative">
        <BoxImage
          src={box.imageUrl}
          alt={box.name}
          className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
          fallback={<span className="text-4xl opacity-40">🎁</span>}
        />
        {box.isCatalog && (
          <Badge variant="secondary" className="absolute top-2.5 left-2.5 rounded-full">
            Catalog
          </Badge>
        )}
      </div>
      <CardContent className="grid gap-3 px-3 pb-3">
        {/* Two lines, always: a one-line and a two-line name must leave the price buttons
            across a row on the same baseline. */}
        <span className="line-clamp-2 min-h-10 text-lg leading-tight font-bold text-foreground">
          {box.name}
        </span>
        <div className="flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-primary px-2.5 text-sm font-medium text-primary-foreground transition-colors group-hover:bg-primary/90">
          <CurrencyIcon currency={currency} className="size-4" />
          {formatAmount(box.priceCents, currency)}
        </div>
        {box.openedCount !== undefined && (
          <span className="text-center text-xs font-bold text-muted-foreground">
            {formatNumber(box.openedCount)} opened
          </span>
        )}
      </CardContent>
    </Card>
  );
}
